import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

/**
 * SwipePager — a labeled swipe-page indicator that makes it OBVIOUS
 * the app has multiple horizontal pages. Replaces tiny ambiguous dots
 * with:
 *   1) A row of 5 readable labels (active highlighted in accent color)
 *   2) A one-shot "← Swipe →" hint that bounces twice for first-visit
 *      users, then auto-hides after 4s. Stored in localStorage so it
 *      doesn't keep showing.
 *   3) Tap targets — each label is a clickable shortcut to that page.
 *
 * Sits absolute-positioned at the bottom of Home.jsx above safe-area.
 */
const PAGE_LABELS = ["Verdict", "Pulse", "Nearby", "Report", "Extremes", "Flood", "Forecast"];
const HINT_KEY = "nowcast.swipe-hint-seen.v1";

export default function SwipePager({ activePage, onScrollTo, theme }) {
  const [showHint, setShowHint] = useState(false);

  // Show the swipe hint on first visit only. Auto-hides after 4s OR when
  // the user manually swipes/taps a label (whichever comes first).
  useEffect(() => {
    try {
      if (!localStorage.getItem(HINT_KEY)) {
        setShowHint(true);
        const t = setTimeout(() => {
          setShowHint(false);
          try { localStorage.setItem(HINT_KEY, "1"); } catch { /* noop */ }
        }, 4000);
        return () => clearTimeout(t);
      }
    } catch { /* noop */ }
  }, []);

  // Hide the hint as soon as the user swipes off Page 1
  useEffect(() => {
    if (activePage !== 0 && showHint) {
      setShowHint(false);
      try { localStorage.setItem(HINT_KEY, "1"); } catch { /* noop */ }
    }
  }, [activePage, showHint]);

  return (
    <div
      className="absolute inset-x-0 z-20 flex flex-col items-center"
      style={{
        bottom: "max(24px, calc(env(safe-area-inset-bottom) + 18px))",
        pointerEvents: "none",
      }}
      data-testid="swipe-pager"
    >
      {/* Swipe hint — only on first visit */}
      <AnimatePresence>
        {showHint && (
          <motion.div
            className="mb-2 inline-flex items-center gap-2 px-3 py-1 rounded-full"
            style={{
              background: theme.surface,
              border: `1px solid ${theme.surfaceBorder}`,
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              color: theme.textMuted,
              boxShadow: "0 6px 20px rgba(15, 23, 42, 0.08)",
            }}
            initial={{ opacity: 0, y: 8, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ type: "spring", stiffness: 280, damping: 22 }}
            data-testid="swipe-hint"
          >
            <motion.span
              animate={{ x: [-3, 0, -3] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            >
              <CaretLeft size={11} weight="bold" />
            </motion.span>
            <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold">
              Swipe through 7 pages
            </span>
            <motion.span
              animate={{ x: [3, 0, 3] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            >
              <CaretRight size={11} weight="bold" />
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Labeled page indicator */}
      <div
        className="inline-flex items-center gap-1 px-2 py-1.5 rounded-full"
        style={{
          background: theme.surface,
          border: `1px solid ${theme.surfaceBorder}`,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
          pointerEvents: "auto",
        }}
      >
        {PAGE_LABELS.map((label, i) => {
          const active = i === activePage;
          return (
            <button
              key={label}
              type="button"
              onClick={() => onScrollTo(i)}
              data-testid={`page-dot-${i}`}
              aria-label={`Go to ${label} page`}
              aria-current={active ? "page" : undefined}
              className="relative px-2 py-0.5 rounded-full transition-colors"
              style={{
                color: active ? "#fff" : theme.textMuted,
              }}
            >
              {active && (
                <motion.span
                  layoutId="swipe-pager-active-bg"
                  className="absolute inset-0 rounded-full"
                  style={{ background: theme.accent }}
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              <span
                className={[
                  "relative z-10 text-[10px] font-semibold tracking-wide whitespace-nowrap",
                  active ? "" : "uppercase",
                ].join(" ")}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
